import { Input, ModalWrapper } from '@/shared';
import React, { useEffect, useState } from 'react';
import { useInviteManager } from '../../hooks/useManagerMutations';
import toast from 'react-hot-toast';

interface InviteHomeManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  house?: any;
}

export const InviteHomeManagerModal: React.FC<InviteHomeManagerModalProps> = ({
  isOpen,
  onClose,
  house: _house,
}) => {
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);

  const handleCancel = () => {
    setEmail('');
    setShowError(false);
    onClose();
  };

  const { mutate: inviteManager, isPending } = useInviteManager();

  const handleInvite = () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setShowError(true);
      return;
    }

    inviteManager(
      { email, houseId: _house?.id },
      {
        onSuccess: () => {
          toast.success('Manager invited successfully');
          setEmail('');
          setShowError(false);
          onClose();
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to invite manager');
        },
      },
    );
  };

  //  Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setShowError(false);
    }
  }, [isOpen]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center w-full min-h-full">
        <div className="w-xl p-4 md:p-8 bg-white rounded-4xl shadow-lg">
          <div className="mb-8">
            <svg
              width="62"
              height="64"
              viewBox="0 0 62 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M55.4784 18.8434V26.8284L60.5494 23.0613L55.4784 18.8434ZM34.0833 1.11889C32.2964 -0.362186 29.7045 -0.378284 27.9176 1.10279L25.873 2.84144H36.1601L34.0833 1.11889Z"
                fill="#1D2486"
              />
              <path
                d="M35.8059 45.4703C34.4214 46.5007 32.7471 47.0641 31.0246 47.048C29.302 47.048 27.6117 46.5007 26.2272 45.4703L0.582121 26.3291V59.1542C0.566023 61.8104 2.70714 63.9837 5.36341 63.9998H56.6375C59.2937 63.9837 61.4348 61.8104 61.4187 59.1542V26.3291L35.8059 45.4703Z"
                fill="#5761EB"
              />
              <path
                d="M9.59766 6.06104V29.1142L28.111 42.8946C29.8336 44.1503 32.2001 44.1503 33.9226 42.8946L52.259 29.2269V6.06104H9.59766ZM21.0277 18.9399H29.7209C30.6063 18.9399 31.3308 19.6644 31.3308 20.5498C31.3308 21.4352 30.6063 22.1596 29.7209 22.1596H21.0277C20.1422 22.1596 19.4178 21.4352 19.4178 20.5498C19.4178 19.6644 20.1422 18.9399 21.0277 18.9399ZM40.9899 30.2089H21.0277C20.1422 30.2089 19.4178 29.4845 19.4178 28.5991C19.4178 27.7137 20.1422 26.9892 21.0277 26.9892H40.9899C41.8754 26.9892 42.5998 27.7137 42.5998 28.5991C42.5998 29.4845 41.8754 30.2089 40.9899 30.2089Z"
                fill="#D9D9D9"
              />
            </svg>
          </div>
          <div className="mb-4 md:mb-8">
            <h4 className="text-xl md:text-3xl font-bold text-text-primary mb-2">
              Invite a house manager
            </h4>
            <p className="text-base md:text-xl text-text-secondary">
              Enter the manager’s email address to invite them. They will be
              able to manage members, update details, and view related records.
            </p>
          </div>

          <div className="mb-4 md:mb-8">
            <Input
              label="Enter Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="!text-base md:text-xl"
              placeholder="example@email.com"
            />
          </div>

          {showError && (
            <p className="text-sm text-red-500 mb-4">
              Please enter a valid email address before sending the invite.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <button
              className="p-3 bg-gray-100 text-gray-700 rounded-lg text-base md:text-lg font-bold cursor-pointer hover:bg-gray-200"
              onClick={handleCancel}
            >
              Cancel
            </button>

            <button
              className="p-3 text-white bg-[#5761EB] text-base md:text-lg font-bold rounded-md hover:bg-[#4048d6] transition-colors"
              disabled={isPending}
              onClick={handleInvite}
            >
              {isPending ? 'Inviting...' : 'Invite Manager'}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
